import insightsDashboardImage from "@/assets/insights-dashboard.webp";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading1, Heading3 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { ChartLineIcon, HandCoinsIcon, UsersIcon } from "lucide-react";
import { type FC, type ReactNode, useCallback } from "react";

export const InsightsEducationPage: FC = () => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: trialRequestedValue, setValueAsync: setTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const trialRequested = trialRequestedValue === "true";

    const handleRequestTrial = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "We'll be in touch about your trial.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: error instanceof Error ? error.message : "An unknown error occurred.",
            });
        }
    }, [account?.email, segmentTrack, setTrialRequestedValue]);

    return (
        <div className="flex flex-col items-center justify-center py-4">
            <div className="w-full max-w-7xl">
                <Pill variant="neutral" size="lg">
                    Available on the Enterprise tier ✨
                </Pill>
                <div className="my-16 flex max-w-full flex-row flex-wrap justify-between gap-8">
                    <div className="max-w-xl">
                        <Heading1 className="mb-4 flex flex-row items-center gap-2">
                            Turn Insights into actionable intelligence
                        </Heading1>
                        <Paragraph className="text-secondary mb-8 text-balance text-lg">
                            Ona Insights provides powerful analytics to monitor, understand, and optimize your cloud
                            development environment usage, enhancing your team&apos;s productivity and
                            cost-effectiveness.
                        </Paragraph>
                        <div className="mt-12 flex flex-col items-center justify-start gap-4 sm:flex-row">
                            <Button
                                size="lg"
                                onClick={handleRequestTrial}
                                disabled={trialRequested}
                                data-tracking-id="request-trial-insights-education-page"
                            >
                                {trialRequested ? "Trial Requested" : "Request trial"}
                            </Button>
                            <Button variant="outline" size="lg" asChild>
                                <ExternalLink href="https://ona.com/docs/ona/organizations/insights" iconSize="sm">
                                    Learn more
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>

                    <img
                        src={insightsDashboardImage}
                        alt="Ona Insights dashboard"
                        className="mb-8 w-[42rem] max-w-full rounded-lg shadow-xl"
                        width={3554}
                        height={2128}
                    />
                </div>

                <div className="flex flex-col gap-8 md:flex-row">
                    <InsightsEducationCard
                        icon={<ChartLineIcon size={24} />}
                        title="Analyze usage across your organization"
                        description="Monitor environment usage patterns in real time. See resource consumption for users,
                            projects, and environment classes."
                    />

                    <InsightsEducationCard
                        icon={<HandCoinsIcon size={24} />}
                        title="Maximize ROI and manage costs"
                        description="Gain actionable intelligence for optimization for user and project activity and their
                            resource allocation."
                    />

                    <InsightsEducationCard
                        icon={<UsersIcon size={24} />}
                        title="Drive team productivity through insights"
                        description="Unlock maximum productivity by reviewing adoption trends and usage patterns across users and
                            projects."
                    />
                </div>
            </div>
        </div>
    );
};

const InsightsEducationCard: FC<{
    icon?: ReactNode;
    title: string;
    description: string;
}> = ({ icon, title, description }) => {
    return (
        <div className="flex flex-col items-center md:items-start">
            {icon}
            <div className="mt-2 flex flex-row items-center gap-2 text-left">
                <Heading3 className="mb-2">{title}</Heading3>
            </div>
            <Paragraph className="text-left text-content-secondary">{description}</Paragraph>
        </div>
    );
};
